import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import toast from 'react-hot-toast';

const NAVY = '#001F3F';
const GOLD = '#B8860B';

export default function StyleLibrary() {
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const user = auth.currentUser;

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // This looks for styles specifically saved by the logged-in user
    const q = query(
      collection(db, 'user_styles'), 
      where('userId', '==', user.uid)
    );

    const unsub = onSnapshot(q, (snap) => {
      const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setStyles(fetched);
      setLoading(false);
    }, (err) => {
      console.error("Library fetch error:", err);
      setLoading(false);
    });

    return () => unsub();
  }, [user, navigate]);

  const handleDelete = async (styleId) => {
    if (!window.confirm("Delete this style from your library?")) return;
    
    try {
      await deleteDoc(doc(db, 'user_styles', styleId));
      toast.success("Style deleted successfully");
    } catch (err) {
      toast.error("Failed to delete style");
      console.error(err);
    }
  };

  if (loading) return <div style={center}><div className="spinner"></div></div>;

  return (
    <div style={container}>
      {/* Navigation Bar */}
      <div style={topNav}>
        <button onClick={() => navigate('/dashboard')} style={backBtn}>← BACK TO DASHBOARD</button>
      </div>

      <header style={header}>
        <h1 style={title}>My Style Library</h1>
        <p style={{ opacity: 0.8 }}>Your curated collection of precision looks.</p>
      </header>

      {styles.length === 0 ? (
        <div style={emptyState}>
          <p>You haven't saved any styles yet.</p>
          <button onClick={() => navigate('/gallery')} style={goldBtn}>Explore Style Vault</button>
        </div>
      ) : (
        <div style={styleGrid}>
          {styles.map((style) => (
            <div key={style.id} style={styleCard}>
              <div style={imageContainer}>
                <img 
                  src={style.image || style.previewUrl} 
                  alt={style.name} 
                  style={cardImg} 
                />
              </div>
              <div style={cardContent}>
                <h3 style={styleTitle}>{style.name || 'Saved Style'}</h3>
                <div style={buttonGroup}>
                  <button 
                    onClick={() => navigate(`/viewer?id=${style.id}`)} 
                    style={tryBtn}
                  >
                    TRY ON
                  </button>
                  <button 
                    onClick={() => handleDelete(style.id)} 
                    style={deleteBtn}
                  >
                    DELETE
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// STYLES
const container = { minHeight: '100vh', background: '#0a0a0a', color: 'white', fontFamily: 'Montserrat, sans-serif', padding: '2rem' };
const topNav = { maxWidth: '1200px', margin: '0 auto 2rem', width: '100%' };
const backBtn = { background: 'none', border: 'none', color: GOLD, fontWeight: 'bold', cursor: 'pointer', fontSize: '0.9rem' };
const header = { textAlign: 'center', marginBottom: '3rem' };
const title = { fontSize: '2.5rem', color: GOLD, fontWeight: '800', marginBottom: '0.5rem' };
const styleGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '2rem', maxWidth: '1200px', margin: '0 auto' };
const styleCard = { background: 'rgba(255,255,255,0.05)', borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(184, 134, 11, 0.2)', transition: 'transform 0.2s' };
const imageContainer = { width: '100%', height: '300px', overflow: 'hidden' };
const cardImg = { width: '100%', height: '100%', objectFit: 'cover' };
const cardContent = { padding: '1.5rem' };
const styleTitle = { margin: '0 0 1rem', fontSize: '1.2rem', fontWeight: 'bold' };
const buttonGroup = { display: 'flex', gap: '10px' };
const tryBtn = { flex: 2, background: GOLD, color: 'black', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' };
const deleteBtn = { flex: 1, background: 'transparent', color: '#ff4d4d', border: '1px solid #ff4d4d', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' };
const emptyState = { textAlign: 'center', padding: '5rem 0' };
const goldBtn = { background: GOLD, color: 'black', border: 'none', padding: '1rem 2rem', borderRadius: '50px', fontWeight: 'bold', cursor: 'pointer', marginTop: '1rem' };
const center = { minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#0a0a0a' };
